/**
 * Scrolls to a [data-consistency-anchor="<anchor>"] element and briefly flashes
 * a ring around it so the user can spot what they need to fix.
 *
 * Used by <ConsistencyWarnings /> when the user clicks "Fix this".
 */
export function scrollAndFlash(anchor: string, level: 'warn' | 'block' = 'warn'): void {
  if (typeof document === 'undefined' || !anchor) return
  // Wait a tick so any tab-switch state update finishes rendering.
  setTimeout(() => {
    const el = document.querySelector(`[data-consistency-anchor="${anchor}"]`) as HTMLElement | null
    if (!el) return
    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    } catch {
      el.scrollIntoView()
    }
    const cls = level === 'block' ? 'consistency-flash-block' : 'consistency-flash-warn'
    el.classList.add(cls)
    setTimeout(() => {
      el.classList.remove(cls)
    }, 2200)
  }, 80)
}

/**
 * CSS used by the flash effect. Inject once at app boot via a `<style>` tag if
 * Tailwind purge would otherwise drop dynamic class names.
 */
export const SCROLL_FLASH_CSS = `
.consistency-flash-warn {
  outline: 2px solid rgb(234 179 8);
  outline-offset: 4px;
  border-radius: 6px;
  transition: outline-color 0.4s ease-out, box-shadow 0.4s ease-out;
  box-shadow: 0 0 0 6px rgba(234, 179, 8, 0.18);
}
.consistency-flash-block {
  outline: 2px solid rgb(220 38 38);
  outline-offset: 4px;
  border-radius: 6px;
  transition: outline-color 0.4s ease-out, box-shadow 0.4s ease-out;
  box-shadow: 0 0 0 6px rgba(220, 38, 38, 0.22);
}
`

let injected = false
export function ensureScrollFlashCSS(): void {
  if (typeof document === 'undefined' || injected) return
  const style = document.createElement('style')
  style.setAttribute('data-consistency-flash', 'true')
  style.textContent = SCROLL_FLASH_CSS
  document.head.appendChild(style)
  injected = true
}
